import { apiUrl } from "../taskpane";

const parseResponse = async (response) => {
  if (response.status === 204) return null;
  const text = await response.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!response.ok) {
    const detail = (data && data.detail) || response.statusText || "Request failed";
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
};

const authHeaders = (token, json = false) => {
  const h = { Authorization: `Bearer ${token}` };
  if (json) h["Content-Type"] = "application/json";
  return h;
};

const qs = (params) => {
  const u = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  });
  const s = u.toString();
  return s ? `?${s}` : "";
};

// ── Units ────────────────────────────────────────────────────────────

export async function fetchUnits(token, dimension = null) {
  const res = await fetch(apiUrl(`catalog/units${qs({ dimension })}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function upsertUnit(token, unit) {
  const res = await fetch(apiUrl("catalog/units"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(unit),
  });
  return parseResponse(res);
}

export async function deleteUnit(token, code) {
  const res = await fetch(apiUrl(`catalog/units/${encodeURIComponent(code)}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

// ── Classifications ──────────────────────────────────────────────────

export async function fetchClassifications(token, { kind = null, parentCode = "__root__", q = "" } = {}) {
  const res = await fetch(apiUrl(`catalog/classifications${qs({ kind, parent_code: parentCode, q })}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function getClassification(token, code) {
  const res = await fetch(apiUrl(`catalog/classifications/${encodeURIComponent(code)}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function upsertClassification(token, payload) {
  const res = await fetch(apiUrl("catalog/classifications"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteClassification(token, code) {
  const res = await fetch(apiUrl(`catalog/classifications/${encodeURIComponent(code)}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

// ── Products ─────────────────────────────────────────────────────────

export async function fetchProducts(token, { kind = null, classificationCode = null, q = "", isGeneric = null, limit = 200 } = {}) {
  const res = await fetch(
    apiUrl(`catalog/products${qs({ kind, classification_code: classificationCode, q, is_generic: isGeneric, limit })}`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function getProduct(token, productId) {
  const res = await fetch(apiUrl(`catalog/products/${productId}`), { headers: authHeaders(token) });
  return parseResponse(res);
}

export async function createProduct(token, payload) {
  const res = await fetch(apiUrl("catalog/products"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateProduct(token, productId, payload) {
  const res = await fetch(apiUrl(`catalog/products/${productId}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteProduct(token, productId) {
  const res = await fetch(apiUrl(`catalog/products/${productId}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

// ── Suppliers ────────────────────────────────────────────────────────

export async function fetchSuppliers(token, { q = "", isActive = null } = {}) {
  const res = await fetch(apiUrl(`catalog/suppliers${qs({ q, is_active: isActive })}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function createSupplier(token, payload) {
  const res = await fetch(apiUrl("catalog/suppliers"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateSupplier(token, id, payload) {
  const res = await fetch(apiUrl(`catalog/suppliers/${id}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteSupplier(token, id) {
  const res = await fetch(apiUrl(`catalog/suppliers/${id}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

// ── Supplier offers (prices) ─────────────────────────────────────────

export async function fetchOffers(token, { productId = null, supplierId = null, activeOn = null } = {}) {
  const res = await fetch(
    apiUrl(`catalog/offers${qs({ product_id: productId, supplier_id: supplierId, active_on: activeOn })}`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function createOffer(token, payload) {
  const res = await fetch(apiUrl("catalog/offers"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateOffer(token, id, payload) {
  const res = await fetch(apiUrl(`catalog/offers/${id}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteOffer(token, id) {
  const res = await fetch(apiUrl(`catalog/offers/${id}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function fetchOfferHistory(token, { productId = null, supplierId = null } = {}) {
  const res = await fetch(
    apiUrl(`catalog/offers/history${qs({ product_id: productId, supplier_id: supplierId })}`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function supersedeOffer(token, offerId, payload) {
  const res = await fetch(apiUrl(`catalog/offers/${offerId}/supersede`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

// ── Bulk upserts (Excel round-trip) ──────────────────────────────────

export async function bulkUnits(token, rows) {
  const res = await fetch(apiUrl("catalog/units/bulk"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ rows }),
  });
  return parseResponse(res);
}

export async function bulkClassifications(token, rows) {
  const res = await fetch(apiUrl("catalog/classifications/bulk"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ rows }),
  });
  return parseResponse(res);
}

export async function bulkProducts(token, rows, { kind = null, idsToDelete = null, deleteMissing = true } = {}) {
  const p = new URLSearchParams();
  if (kind) p.set("kind", kind);
  if (!deleteMissing) p.set("delete_missing", "false");
  const qs = p.toString() ? `?${p}` : "";
  const body = { rows };
  if (idsToDelete) body.ids_to_delete = idsToDelete;
  const res = await fetch(apiUrl(`catalog/products/bulk${qs}`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(body),
  });
  return parseResponse(res);
}

export async function bulkSuppliers(token, rows) {
  const res = await fetch(apiUrl("catalog/suppliers/bulk"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ rows }),
  });
  return parseResponse(res);
}

export async function bulkOffers(token, rows) {
  const res = await fetch(apiUrl("catalog/offers/bulk"), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ rows }),
  });
  return parseResponse(res);
}
